import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { FileSystemState } from '../services/filesystem/types';
import { fileSystemService } from '../services/filesystem/FileSystemService';
import { projectScanner, ProjectStructure } from '../services/filesystem/ProjectScanner';
import { useProjectStore } from './useProjectStore';
import { TranslationFile } from '../types';
import { handleStorage } from '../services/filesystem/HandleStorage';

interface FileSystemStore extends FileSystemState {
    // Actions
    setConnected: (handle: FileSystemDirectoryHandle) => void;
    setDisconnected: () => void;
    setLastError: (error: string | null) => void;
    needsPermission: boolean;
    reGrantPermission: () => Promise<void>;

    // US-19-16: two-phase startup state. isSyncingFromDisk=true while async sync runs after shell rendered.
    isSyncingFromDisk: boolean;

    // US-12-07 follow-up: isInitializing=true during initial handle lookup + permission check.
    // Used by UI to show skeleton instead of "kết nối thư mục" prompt flash on refresh.
    isInitializing: boolean;

    // High-level operations
    initialize: () => Promise<void>;
    syncFromDisk: () => Promise<ProjectStructure | null>;
}

export const useFileSystemStore = create<FileSystemStore>()(
    persist(
        (set, get) => ({
            // State
            isConnected: false,
            rootHandle: null,
            folderName: '',
            lastError: null,
            needsPermission: false,
            isSyncingFromDisk: false,
            isInitializing: true,  // default true — set false at end of initialize()

            // Actions
            initialize: async () => {
                const handle = await handleStorage.getHandle();
                if (!handle) {
                    // No persisted handle — first-time user, show connect prompt (not skeleton)
                    set({ isInitializing: false });
                    return;
                }
                // We have a saved handle, try to verify permission
                try {
                    const permission = await (handle as any).queryPermission({ mode: 'readwrite' });
                    if (permission === 'granted') {
                        // US-19-16: TWO-PHASE STARTUP — set shell state immediately, sync background.
                        // Set isSyncingFromDisk=true BEFORE schedule so skeleton renders on first paint.
                        set({
                            rootHandle: handle,
                            isConnected: true,
                            folderName: handle.name,
                            needsPermission: false,
                            isSyncingFromDisk: true,
                            isInitializing: false,
                        });
                        const { fileSystemPersistence } = await import('../services/shared/fileSystemPersistence');
                        (fileSystemPersistence as any).handle = handle;
                        fileSystemPersistence.cleanupLegacyLogs();

                        // Schedule sync AFTER first paint (rAF + setTimeout to escape paint cycle).
                        // Codex round 1 concern: Promise.resolve().then() runs as microtask BEFORE paint = blocks first frame.
                        const scheduleSync = () => {
                            requestAnimationFrame(() => {
                                setTimeout(() => {
                                    get().syncFromDisk().finally(() => {
                                        set({ isSyncingFromDisk: false });
                                    });
                                }, 0);
                            });
                        };
                        if (typeof requestIdleCallback !== 'undefined') {
                            (requestIdleCallback as any)(scheduleSync, { timeout: 100 });
                        } else {
                            scheduleSync();
                        }
                        // Return immediately — UI renders before sync completes
                        return;
                    } else {
                        // Permission not granted yet - need user gesture to re-grant
                        set({
                            rootHandle: handle,
                            isConnected: false,
                            folderName: handle.name,
                            needsPermission: true,
                            isInitializing: false,
                        });
                    }
                } catch (e) {
                    console.warn('[FS] Failed to query permission:', e);
                    set({
                        rootHandle: handle,
                        isConnected: false,
                        folderName: handle.name,
                        needsPermission: true,
                        isInitializing: false,
                    });
                }
            },

            reGrantPermission: async () => {
                const { rootHandle } = get();
                if (!rootHandle) return;
                try {
                    const hasPermission = await fileSystemService.verifyPermission(rootHandle, 'readwrite');
                    if (hasPermission) {
                        // US-19-16 two-phase: set shell state, sync background
                        set({ isConnected: true, needsPermission: false, isSyncingFromDisk: true });
                        const { fileSystemPersistence } = await import('../services/shared/fileSystemPersistence');
                        (fileSystemPersistence as any).handle = rootHandle;
                        fileSystemPersistence.cleanupLegacyLogs();
                        // Schedule sync after first paint
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                get().syncFromDisk().finally(() => {
                                    set({ isSyncingFromDisk: false });
                                });
                            }, 0);
                        });
                    } else {
                        set({ lastError: 'Permission denied by user' });
                    }
                } catch (e: any) {
                    set({ lastError: e.message });
                }
            },

            setConnected: async (handle) => {
                set({
                    isConnected: true,
                    rootHandle: handle,
                    folderName: handle.name,
                    lastError: null
                });
                await handleStorage.saveHandle(handle);
            },

            setDisconnected: async () => {
                set({
                    isConnected: false,
                    rootHandle: null,
                    folderName: '',
                    lastError: null
                });
                await handleStorage.clearHandle();
            },

            setLastError: (error) => set({ lastError: error }),

            syncFromDisk: async () => {
                const { rootHandle, isConnected } = get();
                if (!isConnected || !rootHandle) return null;

                try {
                    // Verify permission first
                    const hasPermission = await fileSystemService.verifyPermission(rootHandle, 'readwrite');
                    if (!hasPermission) {
                        set({ isConnected: false, lastError: 'Permission denied' });
                        return null;
                    }

                    // Use fileSystemPersistence to load projects (reads project_{id}/ format)
                    const { fileSystemPersistence } = await import('../services/shared/fileSystemPersistence');
                    // Ensure persistence service knows about our handle
                    (fileSystemPersistence as any).handle = rootHandle;

                    // US-19-16: METADATA-ONLY mode for startup. Heavy file content (sourceText, originalFile,
                    // docxParagraphMap, auditResult, semanticMatchResult) lazy-loaded via ensureFileContentLoaded().
                    const fsProjects = await fileSystemPersistence.loadProjects('metadata');

                    if (fsProjects.length > 0) {
                        const projects = fsProjects.map((p: any) => ({
                            ...p,
                            type: p.type || 'train',
                            createdAt: p.createdAt ? new Date(p.createdAt) : new Date()
                        }));

                        const files = fsProjects.flatMap((p: any) => p.files || []).map((f: any) => ({
                            ...f,
                            createdAt: f.createdAt ? new Date(f.createdAt) : new Date(),
                            completedAt: f.completedAt ? new Date(f.completedAt) : undefined,
                            // US-19-16: metadata-only load → contentLoaded=false. Heavy fields lazy-load on demand.
                            contentLoaded: false,
                        }));

                        useProjectStore.getState().setProjectsAndFiles(projects, files);
                        console.log(`[syncFromDisk] Loaded ${projects.length} projects, ${files.length} files (metadata-only)`);
                    }

                    // US-19-16: debug logs lazy-loaded only when DevLab tab opened (admin internal).
                    // Removed from startup path per Codex audit (off the critical hydrate path).

                    return null;
                } catch (error: any) {
                    set({ lastError: error.message });
                    return null;
                }
            }
        }),
        {
            name: 'trans-bctn-fs-store',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                // Only persist folderName for UI display.
                // isConnected MUST NOT be persisted - it depends on rootHandle which can't be in localStorage.
                folderName: state.folderName
            }),
            merge: (persistedState: any, currentState: FileSystemStore) => ({
                ...currentState,
                // Restore folderName from localStorage, but NEVER restore isConnected
                folderName: persistedState?.folderName ?? '',
                isConnected: false,
                rootHandle: null,
                needsPermission: false
            })
        }
    )
);
